import { acceptHMRUpdate, defineStore, storeToRefs } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { assignPackageSelectionKey } from '@/helpers/service-package-rows';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useResourcesStore } from '@/stores/resources';
import { useServicesStore } from '@/stores/services';
import type {
  Appointment,
  AppointmentPart,
  CreateOrderInput,
  Customer,
  OrderItemAttributes,
  OrderPermissions,
  OrderState,
  PrepaidCard,
  PrepaidCardAttributes,
  ServicePackage,
  TransactionAttributes,
} from '@/types';
import { useRegisterStore } from './register';

export type OrderItem = OrderItemAttributes & {
  _uid: number;
  id?: string;
  originalPrice: number;
  discount: number;
  appointmentId?: number;
  prepaidCard?: PrepaidCardAttributes & {
    visits: number;
  };
  transactionCost?: boolean;
  vatRateId?: number;
  vatPercentage?: number;
  disableVat?: boolean;
};

export type OrderTransaction = TransactionAttributes & {
  _uid: number;
  id?: string;
  name?: string;
  amount: number;
  deletable: boolean;
  prepaidCard?: PrepaidCard;
};

export type Order = CreateOrderInput & {
  id?: number;
  items: OrderItem[];
  transactions: OrderTransaction[];
  permissions?: OrderPermissions;
  state?: OrderState;
  cashupId?: number | null;
  fiscalizationFinished: boolean;
  number?: string;
  hasInvoice: boolean;
};

let uid = 1;
let transaction_uid = 1;

export const useRegisterOrderStore = defineStore('register/order', () => {
  const { locationId } = storeToRefs(useLocationsStore());
  const { employees, hasSingleEmployee } = storeToRefs(useResourcesStore());
  const { company } = useCompanyStore();

  const initialState = (): Order => ({
    id: undefined,
    invoicedAt: undefined,
    items: [],
    locationId: locationId.value,
    transactions: [],
    note: '',
    customerId: null,
    originalOrderId: null,
    draft: false,
    fiscalizationFinished: false,
    permissions: {
      credit: true,
      delete: true,
      edit: true,
      editOrderItems: true,
    },
    cashupId: null,
    pending: false,
    number: '',
    state: undefined as OrderState | undefined,
    hasInvoice: false,
  });

  const order = reactive<Order>({ ...initialState() });
  const canBeOutstanding = ref(true);
  const correction = ref(false);

  const cannotSaveOutstanding = computed(() => {
    return !canBeOutstanding.value && outstandingAmount.value > 0;
  });

  const totalPrice = computed(() =>
    order.items.reduce(
      (acc, item) => acc + (item.price || 0) * (item.quantity || 0),
      0,
    ),
  );
  const totalPaid = computed(() =>
    order.transactions.reduce(
      (acc, transaction) => acc + (transaction.amount || 0),
      0,
    ),
  );
  const outstandingAmount = computed(() => totalPrice.value - totalPaid.value);

  const itemResource = computed(() =>
    hasSingleEmployee.value
      ? employees.value[0].id
      : selectedEmployeeId.value !== 'multiple'
        ? selectedEmployeeId.value
        : null,
  );

  const addItem = ({
    id,
    medical = false,
    name,
    price,
    productId,
    serviceId,
    giftcard,
    prepaidCard,
    appointmentPartId,
    appointmentId,
    discount = 0,
    discountCodeId,
    loyaltyPointsAmount = 0,
    originalPrice = price,
    quantity = 1,
    employeeId,
    transactionCost = false,
    vatRateId,
    usedPrepaidCardId,
    medicalReferenceNumber = '',
    vatPercentage,
    disableVat = vatPercentage === null,
    treatmentDate = new Date().toISOString(),
    servicePackageServiceId,
  }: {
    id?: string;
    medical?: boolean;
    name: string;
    price: number;
    productId?: number;
    serviceId?: number;
    giftcard?: any;
    prepaidCard?: any;
    appointmentPartId?: number;
    appointmentId?: number;
    discount?: number;
    discountCodeId?: string;
    loyaltyPointsAmount?: number;
    originalPrice?: number;
    quantity?: number;
    employeeId?: number;
    transactionCost?: boolean;
    vatRateId?: number;
    usedPrepaidCardId?: number;
    medicalReferenceNumber?: string;
    vatPercentage?: number;
    disableVat?: boolean;
    treatmentDate?: string;
    servicePackageServiceId?: number | null;
  }) => {
    // Add a new order item. If an item with the same service/product id already exists, increase its quantity
    // EXCEPT if item is appointment or part of a package
    const existingItem =
      appointmentPartId || servicePackageServiceId
        ? null
        : order.items.find(
            (orderItem: OrderItem) =>
              !orderItem.servicePackageServiceId &&
              ((orderItem.serviceId &&
                serviceId &&
                orderItem.serviceId === serviceId) ||
                (orderItem.productId && orderItem.productId === productId)) &&
              orderItem.price === price,
          );

    if (appointmentId) {
      treatmentDate = orderAppointments.value.find(
        (appointment: Appointment) => appointment.id === appointmentId,
      )?.startAt;
    }

    if (!id && existingItem?.quantity) {
      existingItem.quantity = existingItem.quantity + quantity;
    } else {
      order.items.push({
        _uid: uid,
        id,
        medical: company.medical ? medical : false,
        name,
        price: price || 0,
        productId,
        quantity,
        serviceId,
        giftcard,
        prepaidCard,
        appointmentPartId,
        appointmentId,
        discount:
          originalPrice && !usedPrepaidCardId
            ? discount || 100 - (price * 100) / originalPrice || 0
            : 0,
        discountCodeId,
        originalPrice: originalPrice || 0,
        loyaltyPointsAmount,
        employeeId: transactionCost ? null : employeeId || itemResource.value,
        transactionCost,
        vatRateId,
        usedPrepaidCardId,
        medicalReferenceNumber,
        vatPercentage,
        disableVat,
        treatmentDate,
        servicePackageServiceId,
      });
    }

    uid++;
  };

  const addServicePackage = (servicePackage: ServicePackage) => {
    const { serviceById } = useServicesStore();
    const firstIndex = order.items.length;

    servicePackage.servicePackageServices.forEach((servicePackageService) => {
      const service = serviceById(servicePackageService.service.id);

      addItem({
        name: service?.name ?? '',
        price: servicePackageService.price,
        originalPrice: servicePackageService.originalPrice,
        serviceId: servicePackageService.service.id,
        medical: service?.medical ?? false,
        servicePackageServiceId: servicePackageService.id,
      });
    });

    assignPackageSelectionKey(order.items.slice(firstIndex));
  };

  const duplicateServicePackage = (uids: number[]) => {
    const lines = order.items.filter((item) => uids.includes(item._uid));
    const lastIndex = order.items.findIndex(
      (item) => item._uid === uids[uids.length - 1],
    );

    const copies = lines.map((line) => ({
      ...line,
      id: undefined,
      _uid: uid++,
      quantity: 1,
    }));

    order.items.splice(lastIndex + 1, 0, ...copies);
    assignPackageSelectionKey(copies);
  };

  const removeServicePackage = (uids: number[]) => {
    uids.forEach((itemUid) => {
      removeItem(itemUid);
    });
  };

  const removeItem = (uid: number) => {
    const itemToRemove = order.items.find(
      (item: OrderItem) => item._uid === uid,
    );
    if (itemToRemove?.id) {
      deletedItems.value.push({ ...itemToRemove, destroy: true });
    }
    order.items = order.items.filter((item: OrderItem) => item._uid !== uid);
  };

  const duplicateItem = ({ _uid, price }: { _uid: number; price: number }) => {
    const item: OrderItem | undefined = order.items.find(
      (item: OrderItem) => item._uid === _uid,
    );
    const idx = order.items.findIndex((item: OrderItem) => item._uid === _uid);

    if (item) {
      order.items.splice(idx + 1, 0, {
        ...item,
        id: undefined,
        _uid: uid,
        price,
        quantity: 1,
      });

      uid++;
    }

    uid++;
  };

  const selectedEmployeeId = ref<'multiple' | number | null | undefined>(null);

  const router = useRouter();

  const assignItemResource = () => {
    if (router?.currentRoute.value.name === 'register-payment') {
      return;
    }

    order.items.forEach((item: OrderItem) => {
      if (
        !item.transactionCost &&
        (selectedEmployeeId.value !== 'multiple' || !item.employeeId)
      ) {
        item.employeeId = itemResource.value;
      }
    });
  };

  watch(selectedEmployeeId, assignItemResource);

  const orderAppointments = ref<Appointment[]>([]);

  const selectedCustomer = ref<Customer | null>(null);

  watch(
    () => selectedCustomer.value?.id,
    (customerId) => {
      if (customerId) {
        order.customerId = customerId;
      } else {
        order.customerId = null;

        order.items.forEach((item: OrderItem) => {
          item.loyaltyPointsAmount = 0;
          item.usedPrepaidCardId = null;
        });

        order.transactions = order.transactions.filter(
          (transaction: OrderTransaction) => !transaction.prepaidCardId,
        );

        selectedCustomer.value = null;
      }
    },
  );

  watch(
    () => order.customerId,
    (customerId) => {
      if (!customerId) {
        selectedCustomer.value = null;
      }
    },
  );

  const deletedItems = ref<OrderItem[]>([]);
  const deletedTransactions = ref<OrderTransaction[]>([]);

  const tipAmount = ref<number>(0);

  // Reset the state
  const reset = () => {
    Object.assign(order, initialState());
    selectedEmployeeId.value = null;
    selectedCustomer.value = null;
    orderAppointments.value = [];
    deletedItems.value = [];
    deletedTransactions.value = [];
    tipAmount.value = 0;
    canBeOutstanding.value = true;
    correction.value = false;
  };

  const removeOrderAppointment = (appointmentId: number) => {
    const appointment = orderAppointments.value.find(
      (orderAppointment: Appointment) => orderAppointment.id === appointmentId,
    );
    orderAppointments.value = orderAppointments.value.filter(
      (orderAppointment: Appointment) => orderAppointment.id !== appointmentId,
    );
    const appointmentPartIds =
      appointment?.parts.map((part: AppointmentPart) => part.id) || [];
    const orderItemsToRemove = order.items.filter(
      (item: OrderItem) =>
        (item.appointmentId === appointmentId && item.transactionCost) ||
        appointmentPartIds.includes(item.appointmentPartId as number),
    );
    const transactionsToRemove = order.transactions.filter(
      (transaction: OrderTransaction) =>
        // remove transactions that are not deletable, otherwise customers wont be able to fix it themselves if they get it wrong.
        // POS/SPay is excluded: it can only ever be undone by a real refund at the terminal/Adyen, so unlinking
        // the appointment must leave it in place rather than fabricating an internal-only reversal (SALON-2854).
        // ONLINE is excluded for the same reason: the customer genuinely paid at the gateway, so the
        // prepayment stays on the sale as tender for whatever replaces the unlinked items (SALON-3035).
        transaction.type === 'TREATWELL_ONLINE_PAYMENT',
    );

    orderItemsToRemove.forEach((item: OrderItem) => {
      removeItem(item._uid);
    });
    transactionsToRemove.forEach((transaction: OrderTransaction) => {
      removeTransaction(transaction);
    });

    if (
      !order.id &&
      !order.items.length &&
      !order.transactions.length &&
      !orderAppointments.value.length
    ) {
      reset();
    }
  };

  const addTransaction = ({
    id,
    type,
    amount,
    giftcardId,
    giftyCode,
    name,
    prepaidCardId,
    deletable = true,
    transactionAt,
    prepaidCard,
  }: {
    id?: string;
    type: string;
    amount: number;
    giftcardId?: string;
    giftyCode?: string;
    name?: string;
    prepaidCardId?: string;
    deletable?: boolean;
    transactionAt?: string;
    prepaidCard?: PrepaidCard;
  }) => {
    const existingPrepaidCardTransaction = order.transactions.find(
      (transaction: OrderTransaction) =>
        transaction.prepaidCardId &&
        transaction.prepaidCardId === prepaidCardId,
    );
    if (existingPrepaidCardTransaction) {
      existingPrepaidCardTransaction.amount += amount;
    } else {
      order.transactions.push({
        id,
        type: type as any,
        amount,
        giftcardId,
        giftyCode,
        prepaidCard,
        prepaidCardId,
        name,
        locationId: locationId.value,
        transactionAt,
        deletable,
        _uid: transaction_uid,
      });

      transaction_uid++;
    }
  };

  const removeTransaction = (transaction: OrderTransaction) => {
    const transactionToRemove = order.transactions.find(
      (orderTransaction: OrderTransaction) =>
        orderTransaction._uid === transaction._uid,
    );

    // reset the items prepaid card to null after the prepaid card is removed & set remaining about back
    if (transaction.type === 'PREPAID_CARD') {
      let itemsQuantity = 0;
      order.items.forEach((item: OrderItem) => {
        if (
          Number(item.usedPrepaidCardId) ===
          Number.parseInt(transaction.prepaidCardId as string)
        ) {
          item.usedPrepaidCardId = null;
          item.price = item.originalPrice;
          itemsQuantity += item.quantity || 1;
        }
      });
      if (transactionToRemove?.prepaidCard) {
        transactionToRemove.prepaidCard.remaining += itemsQuantity;
      }
    }

    if (transactionToRemove?.id) {
      deletedTransactions.value.push({ ...transactionToRemove, destroy: true });
    }
    order.transactions = order.transactions.filter(
      (orderTransaction: OrderTransaction) =>
        orderTransaction._uid !== transaction._uid,
    );
  };

  const removeAllTransactions = (transaction: OrderTransaction) => {
    order.transactions.forEach((orderTransaction: OrderTransaction) => {
      if (orderTransaction.id && orderTransaction.type === transaction.type) {
        deletedTransactions.value.push({ ...orderTransaction, destroy: true });
      }
    });

    order.transactions = order.transactions.filter(
      (orderTransaction: OrderTransaction) =>
        orderTransaction.type !== transaction.type,
    );
  };

  const setAppointmentResource = (resourceId: number) => {
    if (!selectedEmployeeId.value || !order.items.length) {
      selectedEmployeeId.value = resourceId;
    }
  };

  const totalTwPayments = computed(() =>
    orderAppointments.value
      .filter((appointment) => appointment.paidThroughTreatwell)
      .reduce(
        (sum, appointment) =>
          sum + (appointment.pricePaidThroughTreatwell || 0),
        0,
      ),
  );

  watch(
    () => locationId.value,
    () => {
      order.locationId = locationId.value;
      selectedEmployeeId.value = null;
      assignItemResource();
    },
  );

  const isEmpty = computed(
    () => !order.items.length && !order.note && !orderAppointments.value.length,
  );

  const activeTransactions = computed(() => {
    const transactions = order.transactions.filter(
      (transaction: OrderTransaction) => !transaction.destroy,
    );
    const { cashupEnabled } = useRegisterStore();
    if (cashupEnabled) {
      return transactions;
    } else {
      return mergeTransactions(transactions);
    }
  });

  const mergeTransactions = (arr: OrderTransaction[]) => {
    const result: any = {};
    arr.forEach((item) => {
      if (result[item.type]) {
        result[item.type].amount += item.amount;
      } else {
        result[item.type] = { type: item.type, amount: item.amount };
      }
    });
    return Object.values(result);
  };

  return {
    order,
    totalPrice,
    addItem,
    removeItem,
    reset,
    duplicateItem,
    addServicePackage,
    duplicateServicePackage,
    removeServicePackage,
    selectedEmployeeId,
    selectedCustomer,
    orderAppointments,
    removeOrderAppointment,
    addTransaction,
    totalPaid,
    outstandingAmount,
    removeTransaction,
    removeAllTransactions,
    setAppointmentResource,
    totalTwPayments,
    deletedItems,
    deletedTransactions,
    isEmpty,
    tipAmount,
    activeTransactions,
    canBeOutstanding,
    cannotSaveOutstanding,
    correction,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useRegisterOrderStore, import.meta.hot),
  );
}
